'use client';
import clsx from 'clsx';
import {
  startTransition,
  useCallback,
  useEffect,
  useState,
  useMemo
} from 'react';
import Price from '../price/Price';
import { useBundleSelection } from '../product/product-context';
import { useScrollDirection } from '@/lib/hooks/use-scroll-direction';
import { useModal } from '@/components/modal/ModalProvider';
import { useCampaign } from '@/app/CampaignContext';

/**
 * This component is used to display the sticky header on the bundle page.
 * Not to be confused with the sticky header on the product page.
 */
export default function HeaderSticky({ atcButtonData }) {
  const bundleState = useBundleSelection();
  const campaign = useCampaign();
  const pricingDetailsData = campaign?.pricing_details;

  const priceSummary = bundleState?.priceSummary;
  const [showHeaderSticky, setHeaderSticky] = useState(false);

  const scrollDirection = useScrollDirection();
  const { showPriceDetails } = useModal();

  const bundleSettings = bundleState?.details?.bundleSettings;
  const bundleOutOfStock = bundleSettings?.outOfStock?.value === 'true';
  const currentOffers = bundleState?.currentOffers;

  const bundleHasOutOfStockItems = useMemo(() => {
    if (!currentOffers) return false;

    return currentOffers.some((offer) => {
      // Check if the product itself is marked as out of stock
      if (offer.product?.outOfStock?.value === 'true') {
        return true;
      }

      // Check if the specific variant is out of stock
      if (offer.variant?.outOfStock?.value === 'true') {
        return true;
      }

      // Check product-level metafields for out of stock status
      const productOutOfStockMetafield = offer.product?.metafields?.find(
        (metafield) => metafield?.key === 'product_out_of_stock'
      );

      if (productOutOfStockMetafield?.value?.toLowerCase() === 'true') {
        return true;
      }

      // Check variant-level metafields for out of stock status
      const variantOutOfStockMetafield = offer.variant?.metafields?.find(
        (metafield) => metafield?.key === 'variant_out_of_stock'
      );

      if (variantOutOfStockMetafield?.value?.toLowerCase() === 'true') {
        return true;
      }

      return false;
    });
  }, [currentOffers]);

  const scrollListener = useCallback(() => {
    const currentCtaPosition =
      document.querySelector('#AddToCart')?.getBoundingClientRect().top +
      window.pageYOffset -
      10;

    startTransition(() => {
      setHeaderSticky(window.scrollY >= currentCtaPosition);
    });
  }, [setHeaderSticky]);

  useEffect(() => {
    window.addEventListener('scroll', scrollListener);
    return () => window.removeEventListener('scroll', scrollListener);
  }, [scrollListener]);

  // button state
  const initialText = atcButtonData?.button_text;
  const initialButtonColor = 'opacity-100';
  const [buttonText, setButtonText] = useState(initialText);
  const [buttonOpacity, setButtonOpacity] = useState(initialButtonColor);

  function handleClick() {
    document.getElementById('AddToCart').click();
    setButtonOpacity('opacity-75');
    setButtonText('Adding');
    setTimeout(() => {
      setButtonText(initialText);
      setButtonOpacity(initialButtonColor);
    }, 2000);
  }

  function handlePriceModalClick(e, bundleState) {
    e.preventDefault();
    // Pass pricing details from campaign context for proper modal content
    showPriceDetails(
      `bundle-header-sticky-${bundleState}`,
      null,
      pricingDetailsData
    );
  }

  const buttonClasses =
    'btn flex h-[50px] items-center justify-center px-4! py-2 text-center w-[140px] min-[840px]:w-[132px] min-[1136px]:w-[240px]';

  if (!showHeaderSticky) {
    return null;
  }

  return (
    <>
      <section data-name="header-sticky">
        <div
          id="header-sticky"
          className={clsx(
            'sticky-wrapper border-dawn-grey-30 fixed left-0 w-full border-b bg-white text-left',
            scrollDirection === 'up'
              ? 'top-[57px] z-40 border-t-2'
              : 'top-0 z-50'
          )}
        >
          <div
            className={clsx(
              'mx-3 my-2 flex items-center justify-between text-left min-[840px]:mx-10 min-[840px]:my-4 min-[1136px]:mx-20 min-[1136px]:my-2'
            )}
          >
            <div className="sticky-title flex min-w-0 flex-col text-left">
              <h1
                className={
                  'product-headline h6 min-[840px]:h5 min-[1136px]:h4 block truncate min-[840px]:mr-5 min-[840px]:inline-block min-[840px]:whitespace-normal'
                }
              >
                {bundleState?.bundleName}
              </h1>
              <div className="flex flex-wrap items-center min-[840px]:hidden">
                <PriceBlock
                  priceSummary={priceSummary}
                  bundleState={bundleState}
                  handlePriceModalClick={handlePriceModalClick}
                />
              </div>
            </div>
            <div className="ml-3 flex w-[140px] shrink-0 items-center justify-end min-[840px]:ml-0 min-[840px]:w-auto">
              <div className="mr-4 hidden items-center min-[840px]:flex">
                <PriceBlock
                  priceSummary={priceSummary}
                  bundleState={bundleState}
                  handlePriceModalClick={handlePriceModalClick}
                />
              </div>
              {bundleHasOutOfStockItems || bundleOutOfStock ? (
                <button
                  className={clsx(buttonClasses, 'btn-disabled')}
                  aria-label="Sorry, this product is out of stock"
                  disabled
                >
                  Out of stock
                </button>
              ) : (
                <button
                  className={clsx(buttonClasses, 'btn-primary', buttonOpacity)}
                  onClick={handleClick}
                >
                  {buttonText}
                </button>
              )}
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

const PriceBlock = ({ priceSummary, bundleState, handlePriceModalClick }) => {
  return (
    <>
      <h2
        className={
          'price-s-label min-[52.5rem]:price-r-label lg:price-l-label inline-block'
        }
      >
        {bundleState?.selectedOption?.value}:
      </h2>
      <Price
        price={priceSummary?.price}
        compareAtPrice={priceSummary?.compareAtPrice}
        priceClassOverride={'ml-1 price-s min-[52.5rem]:price-r lg:price-l'}
        crossedPriceClassOverride={
          'price-s-crossed mr-2 min-[52.5rem]:price-r-crossed lg:price-l-crossed'
        }
      />
      <div
        className="body-xs text-grey-text cursor-pointer leading-6! underline min-[1136px]:pl-1"
        onClick={(e) => handlePriceModalClick(e, bundleState?.bundleName)}
      >
        Price details
      </div>
    </>
  );
};
